import React, { FC } from 'react'
import Paper from '@mui/material/Paper'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { InnerLink } from 'src/components/ui'
import { useQueryParams } from 'src/hooks/ui'
import { DashboardStatisticsContractorsAssignment } from 'src/api'
import { SearchParamsKeys } from 'src/enums'

interface AssignmentRow {
  id: number | null
  name: string
  contractorsNumber: number
}

const AssignmentLink: FC<{ row: AssignmentRow }> = ({ row }) => {
  const [, setQueryParams, , reset] = useQueryParams()
  const assigneeId = `${row.id ?? 'none'}`

  return (
    <InnerLink
      to={`/contractors?${SearchParamsKeys.AssigneeId}=${assigneeId}`}
      onClick={() => {
        // the URL query string is owned by QueryParamsProvider state, so the param
        // must be written there too — the `to` query alone survives only full page loads
        reset()
        setQueryParams({ [SearchParamsKeys.AssigneeId]: assigneeId })
      }}
    >
      {row.name}
    </InnerLink>
  )
}

const columns: GridColDef<AssignmentRow>[] = [
  {
    field: 'name',
    headerName: 'Responsible person',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => <AssignmentLink row={row} />
  },
  {
    field: 'contractorsNumber',
    headerName: 'Contractors',
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  }
]

interface ContractorsAssignmentDashboardProps {
  contractorsAssignment: DashboardStatisticsContractorsAssignment[]
}

export const ContractorsAssignmentDashboard: FC<ContractorsAssignmentDashboardProps> = ({ contractorsAssignment }) => {
  const rows: AssignmentRow[] = contractorsAssignment.map((item) => ({
    id: item.id ?? null,
    name: item.name || 'None',
    contractorsNumber: item.contractorsNumber || 0
  }))

  return (
    <Paper sx={{ width: '100%' }}>
      <DataGridPremium<AssignmentRow>
        autoHeight
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id ?? 'none'}
        hideFooter
        localeText={{ noRowsLabel: 'There is no assignment information' }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)'
          }
        }}
      />
    </Paper>
  )
}
